/** Pages of a site: tree by path, drag sort, status, unpublished changes, duplicate, history, translate, share preview, settings, delete. Route /sites/:id/pages */
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined, EyeInvisibleOutlined, HistoryOutlined, HolderOutlined, LinkOutlined, PlusOutlined, SettingOutlined, TranslationOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { CopyLine, editorPath, fmtDate, listOf, toastErr, useLoad, useSite } from './common';
import { PAGE_TYPES, PageSettingsModal } from './editor/dialogs';
import { AiCreditBadge, AiJobBanner, reportAiError, startAi } from './ai';
import { langName } from './TranslationsCard';
import PageRevisionsDrawer from './PageRevisionsDrawer';

const { Text } = Typography;

const STATUSES = [
    { value: 'published', label: 'Published', badge: 'success' },
    { value: 'draft', label: 'Draft', badge: 'default' },
    { value: 'hidden', label: 'Hidden', badge: 'warning' },
];

/** Roots with nested children: the parent is the nearest ancestor path that is a page. Siblings keep their sort order. */
const buildTree = (pages) => {
    const sorted = pages.filter((p) => p.type !== '404').map((p, i) => ({ p, i })).sort((a, b) => (Number(a.p.sort) || 0) - (Number(b.p.sort) || 0) || a.i - b.i).map((x) => ({ ...x.p }));
    const byPath = new Map(sorted.map((n) => [n.path, n]));
    const roots = [];
    sorted.forEach((n) => {
        const segs = String(n.path).split('/').filter(Boolean);
        let parent = null;
        for (let i = segs.length - 1; i > 0 && !parent; i -= 1) parent = byPath.get(`/${segs.slice(0, i).join('/')}`) || null;
        if (parent) { parent.children = parent.children || []; parent.children.push(n); } else roots.push(n);
    });
    return roots;
};
const flatten = (nodes, out = []) => { nodes.forEach((n) => { out.push(n); if (n.children) flatten(n.children, out); }); return out; };
/** the list holding a node (roots or a children array) */
const siblingsOf = (roots, id) => {
    if (roots.some((n) => n.id === id)) return roots;
    for (const n of roots) {
        const found = n.children && siblingsOf(n.children, id);
        if (found) return found;
    }
    return null;
};

/** "Translate to…": the AI job copies the page to /<lang><path>, translates its texts and links both pages as alternates. */
const TranslatePageModal = ({ page, pages, onClose }) => {
    const { t, i18n } = useTranslation();
    const { site, siteId } = useSite();
    const [lang, setLang] = useState();
    const [busy, setBusy] = useState(false);
    const def = site?.default_lang || 'en';
    const langs = (site?.settings?.languages || []).filter((l) => l && l !== def && l !== page?.lang);
    const exists = (l) => pages.some((p) => p.lang === l && p.path === `/${String(l).toLowerCase()}${page?.path === '/' ? '' : page?.path || ''}`);
    React.useEffect(() => { setLang(langs.find((l) => !exists(l)) || langs[0]); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
    const start = async () => {
        setBusy(true);
        try {
            await startAi(siteId, 'translate', { page_id: page.id, lang });
            window.dispatchEvent(new Event('yc-ai-job-started'));
            onClose();
        } catch (e) { reportAiError(e); } finally { setBusy(false); }
    };
    return (
        <Modal open={!!page} title={t('Translate to…')} okText={t('Translate')} okButtonProps={{ disabled: !lang }} confirmLoading={busy} onCancel={onClose} onOk={start} destroyOnHidden>
            {!langs.length ? <Text type="secondary">{t('No other languages yet.')} {t('Add languages in settings')}</Text> : (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Select style={{ width: '100%' }} value={lang} onChange={setLang}
                        options={langs.map((l) => ({ value: l, label: `${langName(l, i18n?.language)}${exists(l) ? ` · ${t('replaces the existing copy')}` : ''}` }))} />
                    <Text type="secondary" style={{ fontSize: 12 }}>{lang ? `/${String(lang).toLowerCase()}${page?.path === '/' ? '' : page?.path}` : ''}</Text>
                    <AiCreditBadge />
                </Space>
            )}
        </Modal>
    );
};

const Pages = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { site, siteId, api, reload: reloadSite } = useSite();
    const [history, setHistory] = useState(null);
    const [translate, setTranslate] = useState(null);
    const { data, loading, reload, setData } = useLoad(() => api.get('/pages'), [api]);
    const [edit, setEdit] = useState(undefined); // undefined closed, null new, page
    const [collapsed, setCollapsed] = useState([]);
    const [over, setOver] = useState(null); // { id, after } drop marker
    const [preview, setPreview] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const dragId = useRef(null);
    const pages = listOf(data);
    const tree = useMemo(() => buildTree(pages), [pages]);
    const errorPages = pages.filter((p) => p.type === '404');
    const rows = useMemo(() => [...tree, ...errorPages], [tree, pages]); // eslint-disable-line react-hooks/exhaustive-deps
    const expanded = flatten(tree).filter((n) => n.children && !collapsed.includes(n.id)).map((n) => n.id);

    const submit = async (v) => {
        try {
            if (edit) await api.put(`/pages/${edit.id}`, v);
            else {
                const p = await api.post('/pages', { ...v, content: [] });
                setEdit(undefined);
                reloadSite();
                navigate(editorPath(siteId, p.id));
                return;
            }
            setEdit(undefined);
            message.success(t('Saved'));
            reload();
        } catch (e) { toastErr(e); }
    };
    const del = async (p) => {
        try { await api.del(`/pages/${p.id}`); reload(); reloadSite(); } catch (e) { toastErr(e); }
    };
    const duplicate = async (p) => {
        try { await api.post(`/pages/${p.id}/duplicate`); message.success(t('Saved')); reload(); reloadSite(); } catch (e) { toastErr(e); }
    };
    const setStatus = async (p, status) => {
        setData(pages.map((x) => (x.id === p.id ? { ...x, status } : x)));
        try { await api.put(`/pages/${p.id}`, { status }); } catch (e) { toastErr(e); reload(); }
    };
    const dropOn = async (target) => {
        const from = dragId.current;
        dragId.current = null;
        setOver(null);
        if (!from || from === target.id) return;
        const fresh = buildTree(pages);
        const sibs = siblingsOf(fresh, from);
        if (!sibs || !sibs.some((n) => n.id === target.id)) return;
        const at = sibs.findIndex((n) => n.id === target.id);
        const [moved] = sibs.splice(sibs.findIndex((n) => n.id === from), 1);
        sibs.splice(at, 0, moved);
        const items = flatten(fresh).map((n, sort) => ({ id: n.id, sort }));
        const order = new Map(items.map((x) => [x.id, x.sort]));
        setData(pages.map((x) => (order.has(x.id) ? { ...x, sort: order.get(x.id) } : x)));
        try { await api.put('/pages/sort', { items }); } catch (e) { toastErr(e); reload(); }
    };
    const dragProps = (p) => {
        if (p.type === '404') return {};
        return {
            draggable: true,
            onDragStart: (e) => { dragId.current = p.id; e.dataTransfer.effectAllowed = 'move'; },
            onDragEnd: () => { dragId.current = null; setOver(null); },
            onDragOver: (e) => {
                const from = dragId.current;
                const sibs = from && from !== p.id ? siblingsOf(tree, from) : null;
                const at = sibs ? sibs.findIndex((n) => n.id === p.id) : -1;
                if (at < 0) return; // only among siblings: the path decides the parent
                e.preventDefault();
                const after = sibs.findIndex((n) => n.id === from) < at;
                if (over?.id !== p.id || over.after !== after) setOver({ id: p.id, after });
            },
            onDrop: (e) => { e.preventDefault(); dropOn(p); },
            style: { cursor: 'grab', ...(over?.id === p.id ? { boxShadow: `inset 0 ${over.after ? -2 : 2}px 0 #1677ff` } : {}) },
        };
    };
    const sharePreview = async () => {
        setPreviewBusy(true);
        try { setPreview(await api.post('/preview-link')); } catch (e) { toastErr(e); } finally { setPreviewBusy(false); }
    };
    const revokePreview = async () => {
        try { await api.del('/preview-link'); setPreview(null); } catch (e) { toastErr(e); }
    };
    const expiry = preview?.expires_at;
    const changed = pages.filter((p) => p.unpublished).length;

    return (
        <Card title={<Space>{t('Pages')}{site?.published_version && changed > 0 && <Tag color="gold">{t('{{n}} unpublished', { n: changed })}</Tag>}</Space>} size="small" extra={(
            <Space>
                <Button icon={<LinkOutlined />} loading={previewBusy} onClick={sharePreview}>{t('Share preview')}</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setEdit(null)}>{t('New page')}</Button>
            </Space>
        )}>
            <AiJobBanner siteId={siteId} kinds={['translate']} onDone={() => { reload(); reloadSite(); }} />
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} pagination={false} scroll={{ x: 820 }}
                expandable={{
                    expandedRowKeys: expanded,
                    onExpand: (open, p) => setCollapsed((xs) => (open ? xs.filter((id) => id !== p.id) : [...xs, p.id])),
                }}
                onRow={(p) => ({ onDoubleClick: () => navigate(editorPath(siteId, p.id)), ...dragProps(p) })}
                columns={[
                    {
                        title: t('Title'), dataIndex: 'title', render: (v, p) => (
                            <Space size={6}>
                                {p.type !== '404' && <HolderOutlined style={{ color: 'var(--ant-color-text-tertiary, #999)' }} />}
                                <a onClick={() => navigate(editorPath(siteId, p.id))} title={v || p.path}
                                    style={{ display: 'inline-block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{v || p.path}</a>
                                {p.noindex && <Tooltip title={t('Hidden from search engines')}><EyeInvisibleOutlined style={{ color: '#999' }} /></Tooltip>}
                                {p.lang && <Tag>{p.lang}</Tag>}
                                {p.unpublished && site?.published_version && (
                                    <Tooltip title={p.unpublished === 'new' ? t('New, not published yet') : t('Changed since the last publish')}>
                                        <Badge status="warning" data-testid="unpublished-dot" />
                                    </Tooltip>
                                )}
                            </Space>
                        ),
                    },
                    { title: t('Path'), dataIndex: 'path', render: (v) => <Text code style={{ whiteSpace: 'nowrap' }}>{v}</Text> },
                    { title: t('Type'), dataIndex: 'type', width: 130, render: (v) => (v === '404' ? t('Error page') : t(PAGE_TYPES.find((x) => x.value === v)?.label || v)) },
                    {
                        title: t('Status'), dataIndex: 'status', width: 140, render: (v, p) => (p.type === '404' ? null : (
                            <div onDoubleClick={(e) => e.stopPropagation()}>
                                <Select size="small" variant="borderless" style={{ width: 125 }} value={v || 'published'} onChange={(s) => setStatus(p, s)}
                                    options={STATUSES.map((s) => ({ value: s.value, label: <Badge status={s.badge} text={t(s.label)} /> }))} />
                            </div>
                        )),
                    },
                    { title: t('Updated'), dataIndex: 'updated_at', width: 170, render: (v) => fmtDate(v) },
                    {
                        title: '', key: 'a', width: 190, align: 'right', render: (_, p) => (
                            <Space size={0}>
                                <Tooltip title={t('Edit')}><Button type="text" size="small" icon={<EditOutlined />} onClick={() => navigate(editorPath(siteId, p.id))} /></Tooltip>
                                {p.type !== '404' && <Tooltip title={t('Duplicate')}><Button type="text" size="small" icon={<CopyOutlined />} onClick={() => duplicate(p)} /></Tooltip>}
                                <Tooltip title={t('Revisions')}><Button type="text" size="small" icon={<HistoryOutlined />} onClick={() => setHistory(p)} /></Tooltip>
                                {p.type === 'page' && !p.path.includes(':') && <Tooltip title={t('Translate to…')}><Button type="text" size="small" icon={<TranslationOutlined />} onClick={() => setTranslate(p)} /></Tooltip>}
                                <Tooltip title={t('Page settings')}><Button type="text" size="small" icon={<SettingOutlined />} onClick={() => setEdit(p)} /></Tooltip>
                                <Popconfirm title={t('Delete this page?')} onConfirm={() => del(p)}>
                                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </Space>
                        ),
                    },
                ]} />
            <PageSettingsModal open={edit !== undefined} page={edit || null} onClose={() => setEdit(undefined)} onSubmit={submit} />
            <PageRevisionsDrawer page={history} onClose={() => setHistory(null)} onRestored={reload} />
            <TranslatePageModal page={translate} pages={pages} onClose={() => setTranslate(null)} />
            <Modal open={!!preview} title={t('Share preview')} onCancel={() => setPreview(null)} destroyOnHidden
                footer={[
                    <Popconfirm key="r" title={t('Revoke this link?')} okButtonProps={{ danger: true }} onConfirm={revokePreview}><Button danger>{t('Revoke')}</Button></Popconfirm>,
                    <Button key="c" type="primary" onClick={() => setPreview(null)}>{t('Done')}</Button>,
                ]}>
                <CopyLine value={preview?.url} />
                {expiry && <Alert type="info" showIcon style={{ marginTop: 12 }} message={`${t('Expires')}: ${fmtDate(typeof expiry === 'number' ? new Date(expiry * 1000).toISOString() : expiry)}`} />}
            </Modal>
        </Card>
    );
};

export default Pages;
